import { Link } from "react-router-dom";
import { useState } from "react";
import api from "@/lib/api";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  ArrowLeft,
  Plus,
  Trash2,
  ListChecks,
  Repeat,
  PartyPopper,
  Sparkles,
  Clock,
  AlertOctagon,
} from "lucide-react";
import { useTodaysChecklist, taskCompletedOn } from "@/lib/tasks";
import { UrgencyBadge, sortTasks } from "@/lib/urgency";

export default function ChecklistPage() {
  const { tasks, total, done, urgentOpen, todayKey, refresh } = useTodaysChecklist();
  const [title, setTitle] = useState("");
  const [recurring, setRecurring] = useState("none");
  const [time, setTime] = useState("");
  const [urgency, setUrgency] = useState("low");
  const [busy, setBusy] = useState(false);

  const todayLabel = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const addTask = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/tasks", {
        title: title.trim(),
        date: todayKey,
        recurring,
        time: time || null,
        urgency,
      });
      setTitle("");
      setRecurring("none");
      setTime("");
      setUrgency("low");
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (task) => {
    const completed = !taskCompletedOn(task, todayKey);
    const params = task.recurring !== "none" ? { date: todayKey } : {};
    await api.patch(`/tasks/${task.id}`, { completed }, { params });
    refresh();
  };

  const updateUrgency = async (task, newUrgency) => {
    await api.patch(`/tasks/${task.id}`, { urgency: newUrgency });
    refresh();
  };

  const remove = async (task) => {
    await api.delete(`/tasks/${task.id}`);
    refresh();
  };

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const allDone = total > 0 && done === total;

  const sorted = sortTasks(tasks, (t) => taskCompletedOn(t, todayKey));
  const open = sorted.filter((t) => !taskCompletedOn(t, todayKey));
  const completed = sorted.filter((t) => taskCompletedOn(t, todayKey));

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl"
            data-testid="checklist-back-button"
          >
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <ListChecks strokeWidth={1.5} className="size-3.5" />
            Daily checklist
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            {todayLabel}
          </h1>
          <p className="text-base text-muted-foreground">
            The list of things you want to get done today. Aim to finish them all.
          </p>
        </div>

        {/* Urgent alert */}
        {urgentOpen.length > 0 && (
          <div
            className="rounded-2xl border-2 border-destructive/40 bg-destructive/10 p-4 sm:p-5 flex items-start gap-3"
            data-testid="urgent-alert"
          >
            <AlertOctagon strokeWidth={1.5} className="size-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display text-base font-bold">
                {urgentOpen.length} highly urgent task{urgentOpen.length === 1 ? "" : "s"} still open
              </div>
              <div className="text-sm text-muted-foreground">
                {urgentOpen.map((t) => t.title).join(" · ")}
              </div>
            </div>
          </div>
        )}

        {/* Progress */}
        <Card
          className="rounded-2xl"
          style={{
            backgroundImage:
              "radial-gradient(circle at 0% 0%, hsl(var(--primary) / 0.08), transparent 45%)",
          }}
          data-testid="checklist-progress-card"
        >
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Today's progress
                </div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold mt-1">
                  {total === 0 ? (
                    "Add a task to get started"
                  ) : allDone ? (
                    <span className="flex items-center gap-2">
                      <PartyPopper strokeWidth={1.5} className="size-7 text-primary" />
                      All done!
                    </span>
                  ) : (
                    <>
                      {done} <span className="text-muted-foreground">of</span> {total}{" "}
                      checked off
                    </>
                  )}
                </div>
              </div>
              {total > 0 && (
                <div className="flex items-center gap-2">
                  <Sparkles strokeWidth={1.5} className="size-5 text-primary" />
                  <span className="font-display font-black text-4xl">{pct}%</span>
                </div>
              )}
            </div>
            {total > 0 && <Progress value={pct} className="h-2" />}
          </CardContent>
        </Card>

        {/* Add task */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Add a task</CardTitle>
            <CardDescription>
              Set a time and urgency (both optional). Recurring tasks show up every day, weekday, or week.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={addTask} className="space-y-3">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Drink water, 30-min walk, Reply to emails…"
                className="h-11"
                data-testid="checklist-task-input"
              />
              <div className="flex flex-wrap items-end gap-x-5 gap-y-3 sm:gap-x-6">
                <div className="w-[120px] sm:w-[130px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
                    Time
                  </label>
                  <Input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="h-11"
                    data-testid="checklist-task-time"
                  />
                </div>
                <div className="w-[120px] sm:w-[130px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
                    Urgency
                  </label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger className="h-11" data-testid="checklist-task-urgency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[140px] sm:w-[150px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
                    Repeats
                  </label>
                  <Select value={recurring} onValueChange={setRecurring}>
                    <SelectTrigger className="h-11" data-testid="checklist-recurring-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">One-time</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekdays">Weekdays</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <Button
                    type="submit"
                    disabled={busy}
                    className="rounded-xl w-full h-11"
                    data-testid="checklist-add-button"
                  >
                    <Plus strokeWidth={1.5} className="size-4 mr-1.5" />
                    Add
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Task lists */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Tasks</CardTitle>
            <CardDescription>
              {total === 0
                ? "Nothing here yet — add your first task above."
                : `${open.length} open · ${completed.length} done`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {open.length > 0 && (
              <ul className="space-y-1.5" data-testid="checklist-open-list">
                {open.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    todayKey={todayKey}
                    onToggle={toggle}
                    onRemove={remove}
                    onUrgencyChange={updateUrgency}
                  />
                ))}
              </ul>
            )}
            {open.length > 0 && completed.length > 0 && (
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pt-2">
                Completed
              </div>
            )}
            {completed.length > 0 && (
              <ul className="space-y-1.5 opacity-70" data-testid="checklist-done-list">
                {completed.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    todayKey={todayKey}
                    onToggle={toggle}
                    onRemove={remove}
                    onUrgencyChange={updateUrgency}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TaskRow({ task, todayKey, onToggle, onRemove, onUrgencyChange }) {
  const done = taskCompletedOn(task, todayKey);
  const isHigh = task.urgency === "high" && !done;
  return (
    <li
      data-testid={`checklist-task-${task.id}`}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-secondary/60 transition-colors border ${
        isHigh ? "border-destructive/40 bg-destructive/5" : "border-transparent hover:border-border"
      }`}
    >
      <Checkbox
        checked={done}
        onCheckedChange={() => onToggle(task)}
        data-testid={`checklist-checkbox-${task.id}`}
        className="size-5"
      />
      <div className="flex-1 min-w-0">
        <div
          className={`text-sm sm:text-base transition-all flex items-center gap-2 flex-wrap ${
            done ? "line-through text-muted-foreground" : ""
          }`}
        >
          <span>{task.title}</span>
          {task.time && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-medium">
              <Clock strokeWidth={1.5} className="size-3" />
              {task.time}
            </span>
          )}
        </div>
      </div>
      <Select value={task.urgency || "low"} onValueChange={(v) => onUrgencyChange(task, v)}>
        <SelectTrigger
          className="h-7 w-[110px] text-[10px] uppercase tracking-wider font-semibold border-none bg-transparent hover:bg-secondary px-2"
          data-testid={`checklist-urgency-trigger-${task.id}`}
        >
          <UrgencyBadge urgency={task.urgency || "low"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="low">Low</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="high">High</SelectItem>
        </SelectContent>
      </Select>
      {task.recurring !== "none" && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-md px-1.5 py-0.5">
          <Repeat strokeWidth={1.5} className="size-3" />
          {task.recurring}
        </span>
      )}
      <button
        onClick={() => onRemove(task)}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
        data-testid={`checklist-delete-${task.id}`}
        aria-label="Delete task"
      >
        <Trash2 strokeWidth={1.5} className="size-4" />
      </button>
    </li>
  );
}
